import {
    BlockStack,
    DataTable,
    Typography,
    type DataTableColumn,
} from '@pv-frontend/ui-components-web';

/**
 * Plain view model for one audit-trail entry — decoupled from Relay's generated
 * types so this table (and its helpers) are unit-testable without a Relay
 * harness. The screen maps `auditLog` results into it.
 *
 * Mirrors the slice-3 `FeatureToggleAuditEntry`: what happened (`action`) to what
 * (`target`), the value transition (`oldValue` → `newValue`, either side null for
 * a create/clear), who did it (`operator`, the calling principal) and when
 * (`whenUtc`, an ISO-8601 UTC instant).
 */
export interface AuditEntryView {
    id: string;
    action: string;
    target: string;
    oldValue: string | null;
    newValue: string | null;
    operator: string;
    whenUtc: string;
}

const EM_DASH = '—';

/**
 * Render a value transition as `old → new`. A missing side (create with no prior
 * value, or a clear with no new value) shows an em-dash so the direction is still
 * legible; when neither side has a value there is nothing to show.
 */
export function formatChange(oldValue: string | null, newValue: string | null): string {
    const from = oldValue?.trim() ? oldValue : null;
    const to = newValue?.trim() ? newValue : null;
    if (from == null && to == null) return EM_DASH;
    return `${from ?? EM_DASH} → ${to ?? EM_DASH}`;
}

const WHEN_FORMAT = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

/**
 * Format an ISO-8601 instant as a stable, human-readable UTC timestamp
 * (e.g. `4 Jul 2026, 21:34 UTC`). Always UTC so the audit trail reads the same
 * for every operator regardless of locale/timezone. An unparseable value falls
 * back to the raw string rather than showing "Invalid Date".
 */
export function formatWhen(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return `${WHEN_FORMAT.format(date)} UTC`;
}

/**
 * Client-side refinement over the server results: forgiving, case-insensitive
 * substring match on the entry `target` (vault) and `operator`, so those filters
 * narrow instantly and tolerate partial input (unlike an exact-UUID server arg).
 * The flag filter is applied server-side by the query. Exported for direct unit
 * testing.
 */
export function filterAuditEntries(
    entries: readonly AuditEntryView[],
    filters: { vault?: string; operator?: string },
): AuditEntryView[] {
    const vault = filters.vault?.trim().toLowerCase() ?? '';
    const operator = filters.operator?.trim().toLowerCase() ?? '';
    return entries.filter(
        (entry) =>
            (!vault || entry.target.toLowerCase().includes(vault)) &&
            (!operator || entry.operator.toLowerCase().includes(operator)),
    );
}

const columns: DataTableColumn<AuditEntryView>[] = [
    {
        key: 'when',
        header: 'When',
        render: (entry) => (
            <Typography variant="body-sm" tone="text-secondary">
                {formatWhen(entry.whenUtc)}
            </Typography>
        ),
    },
    {
        key: 'action',
        header: 'Action',
        render: (entry) => (
            <BlockStack gap="050">
                <Typography variant="body-md" fontWeight="semibold">
                    {entry.action}
                </Typography>
                <Typography variant="body-sm" tone="text-secondary">
                    {entry.target}
                </Typography>
            </BlockStack>
        ),
    },
    {
        key: 'change',
        header: 'Change',
        render: (entry) => (
            <Typography variant="body-sm">{formatChange(entry.oldValue, entry.newValue)}</Typography>
        ),
    },
    {
        key: 'operator',
        header: 'Operator',
        render: (entry) => <Typography variant="body-sm">{entry.operator}</Typography>,
    },
];

export interface AuditTableProps {
    entries: readonly AuditEntryView[];
    /** Live vault filter; substring-matches the entry target. */
    vault?: string;
    /** Live operator filter; substring-matches the calling principal. */
    operator?: string;
}

/**
 * Read-only audit trail rendered as an accessible data table: action + target,
 * old→new transition, operator and UTC timestamp, newest first (the server
 * orders the read). Presentational only — flag scoping is done by the query;
 * vault / operator refinement is applied here.
 */
export function AuditTable({ entries, vault, operator }: AuditTableProps) {
    const rows = filterAuditEntries(entries, { vault, operator });
    const filtered = Boolean(vault?.trim() || operator?.trim());
    const empty = filtered
        ? 'No audit entries match these filters.'
        : 'No audit entries yet.';

    return (
        <DataTable
            columns={columns}
            rows={rows}
            keyExtractor={(entry) => entry.id}
            emptyState={empty}
            testID="audit-table"
        />
    );
}
